import _ from 'lodash';
import React, { Component } from 'react';
import { Button, Modal, Tabs, Tab, FormCheck } from 'react-bootstrap';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { ClipLoader } from 'react-spinners';
import ReactTable from 'react-table';

import toastr from '../../../toastr';
import modal from '../../typings/Modal';
import Model from '../../model/ShopKeeper.model';
import LoadingPage from '../../../LoadingPage';
import { IPartOnBackorder } from '../../typings/Confirmation';

interface IProps {
  container: Object;
  hide: Function;
  model: Model;
  show: boolean;
}

/* This can be loaded one of two ways:
   1. The "Backorder" footer menu via "View Existing Backorder Status"
   2. Via the "Pending Confirmations" prompt on customer load

   Case 1 is not selectable until confirmation details for the customer have been lazy loaded.
   However, Case 2 requires an explicit check for confirmation lazy load status before attempting
   to show confirmation status.
*/

// We want case insensitive and searching within strings
const filterOverride = ({ id, value }: { id: string; value: string }, row: string[]) => {
  if (id === 'part' && _.get(row, '_original.isFreight', false)) {
    return 'po-freight'.includes(value.toLowerCase());
  } else {
    return row[id] ? row[id].toLowerCase().includes(value.toLowerCase()) : false;
  }
};

@observer
class BackorderStatus extends Component<IProps, {}> {
  @observable activeTab = '1';
  @observable isProcessing = false;
  @observable partsChecked = new Set<string>(); // part uniqueIDs

  anyPartsSellable: boolean | undefined;
  partsOnBackorderFilterParams: Array<{ id: string; value: string }> = [];

  async addSelectedToInvoice() {
    const model = this.props.model;

    // should not happen, but someone may bork code in the future
    if (model.confirmationPartsAddLock) {
      toastr.error('Error', 'Confirmation part add in progress.  Please wait.');
      return;
    }

    const partsToAdd: IPartOnBackorder[] = [];
    let partsAdded = false;
    let stockingPartsAdded = false;

    /* It's possible the user drills down into a confirmation and voids something.  For that reason, we
       walk the currently loaded confirmationDetail (possibly revised since initial load) and cross reference
       it against partsChecked, rather than assuming partsChecked is still fully valid. Voided items will not
       appear in partsOnBackorder after refresh, and will therefoe be inert within partsChecked if previously
       selected.

       Additionally, when drilling into a confirmation, it's possible the user sells something directly from there,
       so we'll also check to make sure any part selected for sale hasn't aleady been added another way.
    */
    model.confirmationDetail.partsOnBackorder.forEach(part => {
      if (this.partsChecked.has(part.uniqueID) && !model.confirmationPartsAddedToInvoice.has(part.uniqueID)) {
        partsAdded = true;
        if (part.nonStocking !== 'Y') {
          stockingPartsAdded = true;
        }

        partsToAdd.push(part);
      }
    });

    if (partsAdded) {
      // async UI lock if any stocking parts added (bulk fetch core/fee/detail data)
      // non-stocking parts have everything we need already and don't require a round trip
      if (stockingPartsAdded) {
        model.confirmationPartsAddLock = true; // shared lock
        this.isProcessing = true; // component UI lock
      }

      await model.addConfirmationPartLineItemsFromBOStatus(partsToAdd);

      // unlock UI
      if (stockingPartsAdded) {
        this.isProcessing = false;
        model.confirmationPartsAddLock = false;
      }
    } else {
      toastr.error('Error', 'No new parts selected to sell.');
    }
  }

  /* Note non-tracked (non-stocking) parts are always selectable within this UI
     since clerks often receive & sell non-tracked parts without going through PSO6.
     The PSO6 receiving process, if used, does explicitly mark @parts as "received"
     despite not touching inventory.

     For that reason, this method only selects @parts in a "R" status (explicitly received),
     while @parts which have not been marked as received will still need to be manually
     selected one at a time.

     This feature observe any react-table filters applied.
  */
  selectAllReceived() {
    runInAction(() => {
      this.props.model.confirmationDetail.partsOnBackorder.forEach(part => {
        if (part.status === 'R') {
          if (this.partsOnBackorderFilterParams.length > 0) {
            const filters = this.partsOnBackorderFilterParams;
            for (const filter of filters) {
              if (part.isFreight && filter.id === 'part') {
                if (!'po-freight'.includes(filter.value.toLowerCase())) {
                  return;
                }
              } else if (!_.get(part, filter.id, '').toLowerCase().includes(filter.value.toLowerCase())) {
                return;
              }
            }
          }
          this.partsChecked.add(part.uniqueID);
        }
      });
    });
  }

  render() {
    const { container, hide, model, show } = this.props;

    // These two checks are pedantically hacky, but shorter than using a lifecycle method.

    /* Cache anyPartsSellable after lazy load completes (initial load or refresh).
       anyPartsSellable is derived from walking the customer's confirmation details to
       see if there's any outstanding confirmation lines to display.
    */
    if (model.confirmationDetailLazyLoaded && _.isUndefined(this.anyPartsSellable)) {
      this.anyPartsSellable = model.confirmationDetail.partsOnBackorder.some(
        o => o.status === 'R' || o.nonStocking === 'Y'
      );
    }

    /* Detects confirmation detail refresh in process and prepares for re-init (refresh button).
       A refresh could come from another modal as well such as after voiding a confirmation.
    */
    if (!model.confirmationDetailLazyLoaded) {
      this.anyPartsSellable = undefined;
      this.partsOnBackorderFilterParams = [];
    }

    /* This ostensibly no-op line triggers mobx to re-render on partChecked set changes.
       For example, from "Select All Received".

       The only read of the set is deep within a react-table cell render function that MobX
       can't detect.  We require react-controlled checkbox inputs for react-table filters
       to work correctly, and are using MobX to manage that state with an obsevable set.
       This line tells MobX to observe partsChecked and re-render on changes.
    */
    this.partsChecked.has('mobx-trigger');

    const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(model.runMode);

    const codExtraColumns =
      !isRO && model.customerParams.isCodAccount
        ? [
            {
              Header: () => <span title="Confirmation Customer Name">Customer Name</span>,
              accessor: 'confirmationName',
              filterable: true,
              width: 175,
            },
            {
              Header: () => <span title="Confirmation Customer Phone">Phone</span>,
              accessor: 'confirmationPhone',
              filterable: true,
              width: 110,
            },
          ]
        : [];

    return (
      <Modal
        backdrop="static"
        size="xl"
        container={container as any}
        dialogClassName="sk-backorderStatusModal"
        onHide={hide as any}
        show={show}
      >
        <Modal.Header>
          <Modal.Title>
            Backorder Status - {isRO ? `Repair Order: ${model.activeRONumber}` : `Customer: ${model.activeCustomerID}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {model.confirmationDetailLazyLoaded ? (
            model.confirmationDetail.statuses.length > 0 ? (
              <Tabs
                defaultActiveKey={this.activeTab}
                id="sk-backorderStatus"
                onSelect={(eventKey: string | null) => {
                  if (eventKey) {
                    this.activeTab = eventKey;
                  }
                }}
              >
                <Tab eventKey="1" title="Backorder Status">
                  <br />
                  <ReactTable
                    className="-striped"
                    data={model.confirmationDetail.statuses}
                    defaultFilterMethod={filterOverride}
                    defaultPageSize={model.confirmationDetail.statuses.length}
                    defaultSorted={[
                      {
                        desc: true,
                        id: 'completed',
                      },
                      {
                        desc: true,
                        id: 'filled',
                      },
                    ]}
                    getTrProps={(state: any, rowInfo: any) => {
                      if (rowInfo) {
                        return {
                          style: {
                            background:
                              _.get(rowInfo, 'row.completed', '') === 'Y'
                                ? '#cfc'
                                : _.get(rowInfo, 'row.filled', '') !== ''
                                ? 'lightyellow'
                                : undefined,
                          },
                        };
                      }
                      return {};
                    }}
                    showPagination={false}
                    style={{
                      maxHeight: '450px',
                    }}
                    columns={[
                      {
                        Cell: props => (
                          <span className="sk-actionLink" onClick={() => model.loadConfirmation(props.value)}>
                            {props.value}
                          </span>
                        ),
                        Header: 'Confirmation',
                        accessor: 'confirmationNo',
                        filterable: true,
                        style: {
                          textAlign: 'center',
                        },
                        width: 95,
                      },
                      {
                        Header: () => <span title="All Items Previously Sold or Ready to Sell Now">Completed</span>,
                        accessor: 'completed',
                        style: {
                          textAlign: 'center',
                        },
                        width: 80,
                      },
                      {
                        Header: () => <span title="Total Part Lines on Confirmation">Total Lines</span>,
                        accessor: 'lineCount',
                        style: {
                          textAlign: 'center',
                        },
                        width: 80,
                      },
                      {
                        Header: () => <span title="# of Lines Ready to Sell">Ready Lines</span>,
                        accessor: 'filled',
                        style: {
                          textAlign: 'center',
                        },
                        width: 90,
                      },
                      {
                        Header: () => <span title="# of Lines Not Yet Sold">Unsold Lines</span>,
                        accessor: 'outstanding',
                        style: {
                          textAlign: 'center',
                        },
                        width: 90,
                      },
                      {
                        Header: () => <span title="# of Lines Backordered by the Vendor">B/O</span>,
                        accessor: 'b/o',
                        style: {
                          textAlign: 'center',
                        },
                        width: 60,
                      },
                      {
                        Header: () => <span title="Confirmation Creation Date">Ordered Date</span>,
                        accessor: 'dateOrdered',
                        filterable: true,
                        style: {
                          textAlign: 'center',
                        },
                        width: 110,
                      },
                      {
                        Header: () => <span title="Date Last Part Line Received">Received Date</span>,
                        accessor: 'dateCompleted',
                        style: {
                          textAlign: 'center',
                        },
                        width: 110,
                      },
                      ...codExtraColumns,
                    ]}
                  />
                </Tab>
                <Tab eventKey="2" title="Parts on Backorder">
                  <br />
                  <ReactTable
                    className="-striped"
                    data={model.confirmationDetail.partsOnBackorder}
                    defaultFilterMethod={filterOverride}
                    defaultPageSize={model.confirmationDetail.partsOnBackorder.length}
                    defaultSorted={[
                      {
                        desc: true,
                        id: 'status',
                      },
                    ]}
                    getTrProps={(state: any, rowInfo: any) => {
                      if (rowInfo) {
                        return {
                          style: {
                            background: _.get(rowInfo, 'row.status', '') === 'R' ? 'lightyellow' : undefined,
                          },
                        };
                      }
                      return {};
                    }}
                    onFilteredChange={filterParams => (this.partsOnBackorderFilterParams = filterParams)}
                    showPagination={false}
                    style={{
                      maxHeight: '450px',
                    }}
                    columns={[
                      {
                        Cell: props => {
                          // props.index is data strucure order, props.viewIndex accounts for sorts
                          if (props.value === 'R' || props.original.nonStocking === 'Y') {
                            const title = props.value === 'R' ? 'Recieved' : 'Non-Tracked Part';
                            const value = props.value === 'R' ? 'R' : 'N';
                            const uniqueID = props.original.uniqueID;
                            const addedToInvoice = model.confirmationPartsAddedToInvoice.has(uniqueID);

                            return (
                              <FormCheck inline={true}>
                                <FormCheck.Input
                                  type="checkbox"
                                  className="sk-useMaterialInput"
                                  disabled={addedToInvoice}
                                  onChange={(evt: any) => {
                                    if (evt.target.checked) {
                                      this.partsChecked.add(uniqueID);
                                    } else {
                                      this.partsChecked.delete(uniqueID);
                                    }
                                  }}
                                  checked={this.partsChecked.has(uniqueID) || addedToInvoice}
                                />
                                <FormCheck.Label title={title}>{value}</FormCheck.Label>
                              </FormCheck>
                            );
                          } else {
                            return <span>{props.value}</span>;
                          }
                        },
                        Header: 'Status',
                        accessor: 'status',
                        style: {
                          textAlign: 'center',
                        },
                        width: 60,
                      },
                      {
                        Header: 'Qty',
                        accessor: 'qty',
                        style: {
                          textAlign: 'center',
                        },
                        width: 40,
                      },
                      {
                        Cell: props =>
                          props.original.isFreight
                            ? 'PO-FREIGHT'
                            : props.original.nonStocking === 'Y'
                            ? `@${props.value}`
                            : props.value,
                        Header: 'Part',
                        accessor: 'part',
                        filterable: true,
                        width: 165,
                      },
                      {
                        Header: 'Description',
                        accessor: 'description',
                        filterable: true,
                        width: 145,
                      },
                      {
                        Header: 'Stkcls',
                        accessor: 'stockClass',
                        filterable: true,
                        style: {
                          textAlign: 'center',
                        },
                        width: 60,
                      },
                      {
                        Header: 'Sell Price',
                        accessor: 'tradePrice',
                        style: {
                          textAlign: 'right',
                        },
                        width: 80,
                      },
                      {
                        Header: 'PPC',
                        accessor: 'ppc',
                        filterable: true,
                        style: {
                          textAlign: 'center',
                        },
                        width: 50,
                      },
                      {
                        Header: 'Cust PO #',
                        accessor: 'customerPoNumber',
                        filterable: true,
                        style: {
                          textAlign: 'center',
                        },
                        width: 90,
                      },
                      {
                        Cell: props => (
                          <span className="sk-actionLink" onClick={() => model.loadConfirmation(props.value)}>
                            {props.value}
                          </span>
                        ),
                        Header: 'Conf #',
                        accessor: 'confirmationNumber',
                        filterable: true,
                        style: {
                          textAlign: 'center',
                        },
                        width: 80,
                      },
                      {
                        Cell: props =>
                          /^HODC|^PRE/.test(props.value) ? (
                            props.value
                          ) : (
                            <span
                              className="sk-actionLink"
                              onClick={() =>
                                modal.open('purchaseOrderDetail', {
                                  lookupKey: props.value,
                                  title: `PO Detail: ${props.value}`,
                                })
                              }
                            >
                              {props.value}
                            </span>
                          ),
                        Header: 'PO #',
                        accessor: 'poNumber',
                        filterable: true,
                        style: {
                          textAlign: 'center',
                        },
                        width: 100,
                      },
                    ]}
                  />
                </Tab>
              </Tabs>
            ) : (
              <div style={{ marginTop: '15px' }}>No backorders found for this customer.</div>
            )
          ) : (
            <LoadingPage />
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="sk-backorderStatusModalFooter">
            <div>
              {model.confirmationDetailLazyLoaded ? (
                <>
                  {this.anyPartsSellable && this.activeTab === '2' && (
                    <>
                      <Button
                        variant="success"
                        disabled={this.isProcessing}
                        onClick={() => this.addSelectedToInvoice()}
                      >
                        {this.isProcessing ? (
                          <>
                            <ClipLoader size={13} color="white" /> PROCESSING
                          </>
                        ) : isRO ? (
                          'Add Selected To Operation'
                        ) : (
                          'Add Selected To Invoice'
                        )}
                      </Button>
                      <Button onClick={() => this.selectAllReceived()} variant="secondary">
                        Select All Received
                      </Button>
                    </>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      model.loadConfirmationStatusDetail();
                    }}
                  >
                    <i className="zmdi zmdi-refresh" /> Refresh
                  </Button>
                  {isRO && model.confirmationDetail.statuses.length > 0 && (
                    <Button
                      variant="warning"
                      title="Change Active Operation"
                      onClick={() => {
                        model.uiBridge.showOperationSelectionModal = true;
                      }}
                    >
                      OP: {model.activeOperation || 'S'}
                    </Button>
                  )}
                </>
              ) : null}
            </div>
            <div>
              <Button
                autoFocus={model.confirmationDetailLazyLoaded && model.confirmationDetail.statuses.length === 0}
                disabled={this.isProcessing}
                onClick={() => hide()}
                variant="secondary"
              >
                Close
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default BackorderStatus;
